import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  {
    title: "Product Pricing",
    questions: [
      "Competitiveness of product pricing",
      "Value for money",
      "Transparency of pricing structure",
    ],
  },
  {
    title: "Product Quality",
    questions: [
      "Build quality and durability",
      "Accuracy and reliability of time capture",
      "Ease of use/interface",
      "Integration with existing systems",
    ],
  },
  {
    title: "Technical Support",
    questions: [
      "Availability of support",
      "Response time",
      "Technical knowledge/expertise",
      "Issue resolution effectiveness",
    ],
  },
  {
    title: "Customer Service",
    questions: [
      "Communication and responsiveness",
      "Professionalism",
      "Order handling and delivery",
      "After-sales support",
    ],
  },
  {
    title: "Business & Partnership Factors",
    questions: [
      "Lead times and availability",
      "Product range and innovation",
      "Training and documentation provided",
      "Warranty and returns process",
    ],
  },
  {
    title: "Overall Evaluation",
    questions: [
      "Overall satisfaction",
      "Likelihood to recommend",
    ],
  },
];

const RATING_LABELS = {
  1: "Poor",
  2: "Fair",
  3: "Average",
  4: "Good",
  5: "Excellent",
};

function RatingSelector({ value, onChange, name }) {
  return (
    <div className="flex gap-1 sm:gap-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg text-sm font-semibold transition-all ${
            value === n
              ? "bg-green-600 text-white shadow-md scale-105"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
          data-testid={`${name}-rating-${n}`}
          title={RATING_LABELS[n]}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

export default function CustomerFeedback() {
  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [improvements, setImprovements] = useState("");
  const [followUp, setFollowUp] = useState(null);
  const [regularCatchups, setRegularCatchups] = useState(null);
  const [additionalComments, setAdditionalComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const setRating = (section, question, value) => {
    const key = `${section}::${question}`;
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

  const getRating = (section, question) => {
    return ratings[`${section}::${question}`] || 0;
  };

  const setComment = (section, value) => {
    setComments((prev) => ({ ...prev, [section]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyName.trim()) {
      setError("Company name is required.");
      return;
    }
    setError("");
    setSubmitting(true);

    const sectionData = SECTIONS.map((s) => ({
      title: s.title,
      ratings: s.questions.map((q) => ({
        question: q,
        rating: getRating(s.title, q),
      })),
      comments: comments[s.title] || "",
    }));

    try {
      await axios.post(`${API}/feedback/submit`, {
        company_name: companyName.trim(),
        contact_name: contactName.trim(),
        sections: sectionData,
        improvements: improvements.trim(),
        follow_up_meeting: followUp,
        regular_catchups: regularCatchups,
        additional_comments: additionalComments.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Feedback submission failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md text-center" data-testid="feedback-success">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h2>
          <p className="text-slate-600">
            Your feedback is very important to us. Thank you for taking the time to complete this questionnaire.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/zkteco-logo.png" alt="ZKTeco" className="h-20 w-auto" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="feedback-title">
            Time & Attendance Hardware
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mt-1">Customer Evaluation Questionnaire</p>
          <p className="text-sm text-slate-400 mt-2">
            Please rate each item on a scale of 1 to 5
          </p>
          <div className="flex justify-center gap-4 mt-2 text-xs text-slate-500">
            <span>1 = Poor</span>
            <span>2 = Fair</span>
            <span>3 = Average</span>
            <span>4 = Good</span>
            <span>5 = Excellent</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* General Info */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 sm:p-6 mb-5">
            <h2 className="text-base font-bold text-slate-800 mb-4">General Information</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-sm text-slate-600">Company Name *</Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your company name"
                  className="mt-1"
                  data-testid="feedback-company-name"
                  required
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Contact Name (Optional)</Label>
                <Input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1"
                  data-testid="feedback-contact-name"
                />
              </div>
            </div>
          </div>

          {/* Rating Sections */}
          {SECTIONS.map((section) => (
            <div
              key={section.title}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 sm:p-6 mb-5"
              data-testid={`feedback-section-${section.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <h2 className="text-base font-bold text-slate-800 mb-4">{section.title}</h2>
              <div className="space-y-4">
                {section.questions.map((q) => (
                  <div key={q} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <span className="text-sm text-slate-700 flex-1">{q}</span>
                    <RatingSelector
                      value={getRating(section.title, q)}
                      onChange={(v) => setRating(section.title, q, v)}
                      name={`${section.title}-${q}`.replace(/\s+/g, "-").toLowerCase()}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <Label className="text-xs text-slate-500">Comments (optional)</Label>
                <Textarea
                  value={comments[section.title] || ""}
                  onChange={(e) => setComment(section.title, e.target.value)}
                  placeholder="Any additional comments for this section..."
                  className="mt-1 text-sm"
                  rows={2}
                />
              </div>
            </div>
          ))}

          {/* Additional Feedback */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 sm:p-6 mb-5" data-testid="feedback-section-additional">
            <h2 className="text-base font-bold text-slate-800 mb-4">Additional Feedback</h2>

            <div className="mb-5">
              <Label className="text-sm text-slate-700">What improvements would you like to see?</Label>
              <Textarea
                value={improvements}
                onChange={(e) => setImprovements(e.target.value)}
                placeholder="Your suggestions..."
                className="mt-1 text-sm"
                rows={3}
                data-testid="feedback-improvements"
              />
            </div>

            <div className="mb-5">
              <Label className="text-sm text-slate-700">
                Would you like us to arrange a follow-up face-to-face meeting to discuss your responses in more detail?
              </Label>
              <div className="flex gap-3 mt-2">
                <Button
                  type="button"
                  variant={followUp === true ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFollowUp(true)}
                  data-testid="feedback-followup-yes"
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={followUp === false ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFollowUp(false)}
                  data-testid="feedback-followup-no"
                >
                  No
                </Button>
              </div>
            </div>

            <div className="mb-5">
              <Label className="text-sm text-slate-700">
                Would you welcome regular catch-up meetings to discuss all relevant issues, requests, and ideas?
              </Label>
              <div className="flex gap-3 mt-2">
                <Button
                  type="button"
                  variant={regularCatchups === true ? "default" : "outline"}
                  size="sm"
                  onClick={() => setRegularCatchups(true)}
                  data-testid="feedback-catchups-yes"
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={regularCatchups === false ? "default" : "outline"}
                  size="sm"
                  onClick={() => setRegularCatchups(false)}
                  data-testid="feedback-catchups-no"
                >
                  No
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm text-slate-700">Any other comments</Label>
              <Textarea
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                placeholder="Anything else you'd like to share..."
                className="mt-1 text-sm"
                rows={3}
                data-testid="feedback-additional-comments"
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 mb-4" data-testid="feedback-error">{error}</p>
          )}

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700"
            data-testid="feedback-submit-btn"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
            Submit Feedback
          </Button>

          <p className="text-center text-xs text-slate-400 mt-4">
            Thank you for your time. Your feedback is very important to us.
          </p>
        </form>
      </div>
    </div>
  );
}
